// Convert a live chat object (with closures, live refs, mutating
// child-state arrays) into a JSON-safe payload for IndexedDB, and
// reverse it back into a chat suitable for React state on hydrate.
//
// Single source of truth for the "what counts as JSON-safe" rules is
// `makeReplacer` in exportChatsBundle.js — both this module and the
// `__exportChats()` devtools hook reuse it.

import { transcriptOf } from "./agentTranscript.js";
import { makeReplacer } from "./exportChatsBundle.js";
import { PYTHON_REFRESH_NOTIFICATION_NOTE } from "./pythonExecutionSystemPrompt.js";
import { wasInterrupted } from "./chatSyncSerialize.js";

// The one user-visible mark that a run was cut off. Exported so any
// future list/tab indicator renders the same wording the composer does.
export const INTERRUPTED_BANNER =
    "The page refreshed while this chat was running. In-progress tools stopped. Continue typing to resume.";

// v2: turn snapshots and agentMessages are stored as indices into a
// shared `messagePool` instead of inline copies (see poolSnapshots).
// v1 records (inline snapshots) still hydrate via expandPooledChatRecord's
// passthrough.
const SCHEMA_VERSION = 2;

export function isSupportedSchemaVersion(v) {
    return v === 1 || v === 2;
}

// Record-level gate for anything read back from chatStorage — the
// single decision ChatConsole applies before hydrating a body or
// backfilling meta from a raw record. Kept here (not inlined at the
// call sites) so the version-support contract is unit-tested next to
// the version it guards.
export function isLoadableChatRecord(record) {
    return !!record && isSupportedSchemaVersion(record.schemaVersion);
}

// Sentinel embedded in the text of a refresh-notification message so
// subsequent hydrates can strip the previous one before injecting a
// fresh one (otherwise notifications would multiply across many
// refreshes of the same chat).
const REFRESH_NOTIFICATION_MARKER = "[chat-persistence:page-refresh]";

// Build a clean payload for `chatStorage.saveChat`. The `agent`,
// `canvas`, and `smallToolResults` args are pulled by the caller
// (ChatConsole) since they live outside the chat React object:
//
//   agent              → agentsRef.current.get(chatId)
//   canvas             → canvasStore.readCanvas(chatId)
//   smallToolResults   → (Phase 2 — currently passed as {})
//
// Returns a flat object ready to drop into IndexedDB.
export function serializeChatForStorage(chat, { agent, canvas, smallToolResults } = {}) {
    if (!chat) return null;
    // Pool BEFORE jsonClean. Every completed turn's `snapshot` (and the
    // live agentMessages) share message objects by reference in memory,
    // so the real history exists once; JSON has no references, so
    // cleaning/stringifying the chat as-is would materialize one copy
    // of the history PER TURN — a single oversized tool result once
    // snowballed a 13-turn chat into a ~400MB record that crashed the
    // browser. Pooling swaps each snapshot for index arrays into a
    // shared messagePool while identity is still intact, making the
    // persisted size O(history), not O(turns × history).
    const { items: pooledItems, pool, indexOf } = poolSnapshots(chat.items || []);
    // The conversation alone: the prompt and tools are the live agent's, rebuilt
    // with it, and are not the chat's to store.
    const liveMessages = Array.isArray(agent?.state?.messages) ? transcriptOf(agent) : [];
    const agentMessagesRef = liveMessages.map(indexOf);
    const cleanedChat = jsonClean({ ...chat, items: pooledItems });
    const cleanedPool = jsonClean(pool);
    const cleanedCanvas = jsonClean(canvas ?? null);

    return {
        chatId: String(chat.id),
        schemaVersion: SCHEMA_VERSION,
        // Server chat UUID (chat persistence). Additive, orthogonal to the
        // local numeric id + id fence — maps this per-browser chat onto its
        // server row. Null until minted at creation/backfill.
        serverChatId: chat.serverChatId ?? null,
        // Chat "kind" — null for a normal chat, "scout" for a tenant-
        // profiling run. Persisted so the saved tab restores rendered as
        // a profile view rather than a raw transcript.
        kind: cleanedChat.kind ?? null,
        title: cleanedChat.title || "New chat",
        items: stripTransientFromItems(cleanedChat.items || []),
        toggled: cleanedChat.toggled || {},
        // `input` is deliberately not persisted. Persisting the
        // half-typed buffer caused a full chat re-serialize on every
        // keystroke pause — for image-heavy chats that's hundreds of
        // ms each. The downside (half-typed messages lost on refresh)
        // matches every other chat app and is fine.
        thinkingLevel: cleanedChat.thinkingLevel || null,
        scopeMode: cleanedChat.scopeMode ?? null,
        skipScope: !!cleanedChat.skipScope,
        messagePool: Array.isArray(cleanedPool) ? cleanedPool : [],
        agentMessagesRef,
        canvas: cleanedCanvas,
        smallToolResults: smallToolResults || {},
        busyAtClose: !!chat.busy,
        savedAt: Date.now(),
    };
}

// Every field `serializeChatForStorage` emits, minus `savedAt`, in a fixed
// order. Exported so a test can pin it against the serializer -- add a field
// there without adding it here and the fingerprint stops seeing changes to it.
export const CHAT_FINGERPRINT_FIELDS = [
    "chatId",
    "schemaVersion",
    "serverChatId",
    "kind",
    "title",
    "items",
    "toggled",
    "thinkingLevel",
    "scopeMode",
    "skipScope",
    "messagePool",
    "agentMessagesRef",
    "canvas",
    "smallToolResults",
    "busyAtClose",
];

// Content fingerprint of a serialized payload, for "did anything persistable
// actually change since the last save" comparisons.
//
// Two things it must ignore, each for its own reason:
//
//   * `savedAt`, which `serializeChatForStorage` stamps with a fresh
//     `Date.now()` on every call. Comparing raw payload JSON therefore always
//     reports a change, which silently defeats any caller that gates work on the
//     result -- including the sync layer's boundary guard, whose whole purpose
//     is to keep an unchanged exit flush (fired on every tab blur) from POSTing
//     a no-op and bumping the server revision.
//   * every field that is NOT the serializer's own output. A record read back
//     from storage is a SUPERSET of what serialize produces: `chatStorage`
//     decorates each row with its key fields (`tenantId` / `localId`), and the
//     server-restore path adds head state (`interrupted`,
//     `truncatedBeforeOrdinal`, `needsServerBody`, `serverTurnCount`). Diffing
//     whole objects therefore reports "changed" for a stored record and a
//     freshly-serialized payload of identical content -- which is exactly the
//     comparison the save boundary makes on the first pass after a page load.
//
// Projecting onto a fixed field list handles both, and keeps the fingerprint
// meaningful no matter who else decorates the record on the way through.
// Returns null when the payload cannot be stringified, so callers can
// distinguish "no fingerprint" from a real value.
export function chatSaveFingerprint(payload) {
    if (!payload) return null;
    try {
        return JSON.stringify(CHAT_FINGERPRINT_FIELDS.map((field) => payload[field] ?? null));
    } catch {
        return null;
    }
}

// Reverse: given a persisted payload, produce a chat object slot-able
// into ChatConsole's chats React state. Transient runtime fields
// (busy, error, abort handles, staged inputs) are intentionally NOT
// restored — whatever was in flight at save time is gone.
//
// Returns { chat, hydration } where:
//   chat       — JSON-safe chat ready for setChats
//   hydration  — out-of-band data the agent-mount effect needs to
//                replay into the freshly built agent + canvas store
//
// Caller filters unsupported schemaVersion before invoking.
export function hydrateChatFromStorage(payload) {
    if (!payload || typeof payload !== "object") return null;
    payload = isPooledChatRecord(payload)
        ? expandPooledChatRecord(payload)
        : restoreMessageSharing(payload);
    const idNum = Number(payload.chatId);
    const id = Number.isFinite(idNum) && String(idNum) === String(payload.chatId)
        ? idNum
        : payload.chatId;

    // Page-refresh repair: clean up the persisted agentMessages so
    // the next prompt doesn't trip pi-agent / the upstream provider.
    //   (a) Strip the previous refresh-notification (if any) so it
    //       doesn't multiply across refreshes.
    //   (b) Stitch synthetic toolResult messages for any orphan
    //       assistant toolCalls (i.e. toolCall blocks with no matching
    //       toolResult). Providers reject tool-use blocks without
    //       paired results; pi-agent's continue() would also bail.
    //   (c) Conditionally append a fresh refresh-notification when
    //       busyAtClose tripped (in-flight work was lost) OR when
    //       the chat ran any execute_python (Pyodide namespace was
    //       wiped by the refresh; the LLM needs to know its prior
    //       variables are gone).
    const rawMessages = Array.isArray(payload.agentMessages) ? payload.agentMessages : [];
    const filtered = stripPriorRefreshNotifications(rawMessages);
    const stitched = stitchOrphanToolCalls(filtered);
    const usedPython = detectExecutePython(stitched);
    const wantsRefreshNotice = !!payload.busyAtClose || usedPython;
    const agentMessages = wantsRefreshNotice
        ? [...stitched, makeRefreshNotification({ busyAtClose: !!payload.busyAtClose, usedPython })]
        : stitched;

    const chat = {
        id,
        serverChatId: payload.serverChatId ?? null,
        kind: payload.kind ?? null,
        title: payload.title || "New chat",
        // Items get a double-pass: stripTransient downgrades any
        // running statuses left over from a mid-flight refresh,
        // restoreImagePreviewUrls re-derives the data-URL field that
        // serialize stripped (UI's <img src> reads it).
        items: restoreImagePreviewUrls(stripTransientFromItems(payload.items || [])),
        // `input` deliberately not restored — see serializeChatForStorage.
        input: "",
        busy: false,
        // Surface a one-line banner so the user can see at a glance
        // that something was interrupted. Cleared on the next prompt
        // (chat.error is reset in the submit path). Keyed on the durable
        // marks — the newest turn's page_refresh stopReason and the
        // server head's `interrupted` — not just transient busyAtClose,
        // which the first post-reopen save resets to false. The refresh
        // NOTIFICATION above stays keyed on busyAtClose alone: promoting
        // it would flip the save fingerprint on every load of a
        // still-interrupted chat and re-inject model-visible text.
        error: wasInterrupted(payload) || payload.interrupted
            ? INTERRUPTED_BANNER
            : null,
        toggled: payload.toggled || {},
        thinkingLevel: payload.thinkingLevel || null,
        scopeMode: payload.scopeMode ?? null,
        skipScope: !!payload.skipScope,
        staged: [],
        stalledSince: null,
    };
    const hydration = {
        agentMessages,
        canvas: payload.canvas ?? null,
        smallToolResults: payload.smallToolResults || {},
        busyAtClose: !!payload.busyAtClose,
    };
    return { chat, hydration };
}

// Drop any prior refresh-notification messages so a chat that's been
// refreshed N times doesn't accumulate N notifications in its history.
// Sentinel match is exact substring on text — refresh notifications
// always include `REFRESH_NOTIFICATION_MARKER`.
function stripPriorRefreshNotifications(messages) {
    return messages.filter((m) =>
        !(m?.role === "notification"
            && typeof m.text === "string"
            && m.text.includes(REFRESH_NOTIFICATION_MARKER)),
    );
}

// Walk `messages` and inject a synthetic toolResult right after any
// orphan toolCall. An orphan is a toolCall content block whose `id`
// isn't matched by a downstream toolResult message — which happens
// when a tool was mid-execution at the moment of page refresh.
// pi-agent's `continue()` and upstream providers both reject runs
// with orphan tool calls, so we have to repair the gap before the
// next LLM call.
function stitchOrphanToolCalls(messages) {
    const completedIds = new Set();
    for (const m of messages) {
        if (m?.role === "toolResult" && m.toolCallId) {
            completedIds.add(m.toolCallId);
        }
    }
    const result = [];
    for (const m of messages) {
        result.push(m);
        if (m?.role !== "assistant") continue;
        const content = Array.isArray(m.content) ? m.content : [];
        for (const c of content) {
            if (c?.type === "toolCall" && c.id && !completedIds.has(c.id)) {
                result.push({
                    role: "toolResult",
                    toolCallId: c.id,
                    toolName: c.name || "tool",
                    content: [{ type: "text", text: "tool execution interrupted by page refresh" }],
                    isError: true,
                    timestamp: Date.now(),
                });
                // Mark as completed so we don't double-stitch if the
                // assistant message has multiple toolCalls with the
                // same id (shouldn't happen, but defensive).
                completedIds.add(c.id);
            }
        }
    }
    return result;
}

function detectExecutePython(messages) {
    if (!Array.isArray(messages)) return false;
    for (const m of messages) {
        if (m?.role !== "assistant") continue;
        const content = Array.isArray(m.content) ? m.content : [];
        for (const c of content) {
            if (c?.type === "toolCall" && c.name === "execute_python") return true;
        }
    }
    return false;
}

function makeRefreshNotification({ busyAtClose, usedPython }) {
    const parts = [`${REFRESH_NOTIFICATION_MARKER}`];
    if (busyAtClose) {
        parts.push("The chat was mid-run when the page refreshed; any tool calls that were executing have been marked as interrupted (isError) and the agent loop is idle. The user is about to resume.");
    } else {
        parts.push("The chat was resumed from a prior session.");
    }
    if (usedPython) {
        parts.push(PYTHON_REFRESH_NOTIFICATION_NOTE);
    }
    return {
        role: "notification",
        text: parts.join(" "),
        timestamp: Date.now(),
    };
}

// Deduplicate snapshot messages by object identity into a shared pool.
// Items with an Array `snapshot` (completed turns, /context and
// /compact system nodes) get it replaced by `snapshotRef` — an array
// of pool indices. The returned `indexOf` keeps assigning into the
// same pool so the caller can pool agentMessages against the same
// entries (the live history and the last turn's snapshot are usually
// the identical objects).
//
// Degrades gracefully: a message that lost identity-sharing somewhere
// (a deep-copied snapshot) simply pools as an extra entry — correctness
// never depends on the sharing, only the size win does.
function poolSnapshots(items) {
    const pool = [];
    // Map keys by SameValueZero: objects dedupe by identity, which is
    // exactly the sharing contract snapshots have in memory.
    const indexByMsg = new Map();
    const indexOf = (m) => {
        if (indexByMsg.has(m)) return indexByMsg.get(m);
        const idx = pool.length;
        pool.push(m);
        indexByMsg.set(m, idx);
        return idx;
    };
    const mapped = (Array.isArray(items) ? items : []).map((it) => {
        if (!it || typeof it !== "object" || !Array.isArray(it.snapshot)) return it;
        const { snapshot, ...rest } = it;
        return { ...rest, snapshotRef: snapshot.map(indexOf) };
    });
    return { items: mapped, pool, indexOf };
}

// A record is pooled-format if ANY pooled artifact is present — not
// just messagePool. Dispatching on the pool alone would send a corrupt
// record (pool lost, `snapshotRef` still on items) down the v1 path,
// which only recognizes `snapshot` and would silently carry the
// dangling ref fields into React state. The pooled expander handles a
// missing pool gracefully instead: every lookup misses, snapshots
// hydrate empty, and the ref fields are stripped.
function isPooledChatRecord(payload) {
    if (Array.isArray(payload.messagePool) || Array.isArray(payload.agentMessagesRef)) return true;
    return Array.isArray(payload.items)
        && payload.items.some((it) => it && typeof it === "object" && Array.isArray(it.snapshotRef));
}

// Reverse of poolSnapshots, run at the top of hydration. v1 records
// (no pooled artifacts) pass through untouched. Expansion restores the
// in-memory sharing too: pool[i] is the same object everywhere it's
// referenced, so a hydrated chat re-serializes with full dedup.
export function expandPooledChatRecord(payload) {
    if (!payload || typeof payload !== "object") return payload;
    if (!isPooledChatRecord(payload)) return payload;
    const pool = Array.isArray(payload.messagePool) ? payload.messagePool : [];
    const lookup = (idx) =>
        (Number.isInteger(idx) && idx >= 0 && idx < pool.length ? pool[idx] : null);
    const items = (Array.isArray(payload.items) ? payload.items : []).map((it) => {
        if (!it || typeof it !== "object" || !Array.isArray(it.snapshotRef)) return it;
        const { snapshotRef, ...rest } = it;
        return { ...rest, snapshot: snapshotRef.map(lookup).filter(Boolean) };
    });
    // Prefer refs; a mangled record carrying a plain v1-style
    // agentMessages array (and no refs) keeps it rather than losing
    // the history to an empty expansion.
    const agentMessages = Array.isArray(payload.agentMessagesRef)
        ? payload.agentMessagesRef.map(lookup).filter(Boolean)
        : (Array.isArray(payload.agentMessages) ? payload.agentMessages : []);
    const { messagePool: _pool, agentMessagesRef: _refs, ...rest } = payload;
    return { ...rest, items, agentMessages };
}

// v1 records were persisted with snapshots inlined, so parsing them
// yields a DISTINCT object per appearance — the identity sharing that
// poolSnapshots depends on doesn't survive a v1 round-trip, and a
// legacy chat would otherwise re-save with every historical duplicate
// pooled as its own entry (no size win, ever). Rebuild the sharing by
// deduping structurally identical messages onto one canonical object:
// the hydrated chat holds one copy in RAM and its first v2 re-save
// collapses the duplication for good. Keyed on the full JSON string —
// a per-load cost proportional to the record we just parsed anyway,
// and v2 records skip this path entirely.
function restoreMessageSharing(payload) {
    const canonical = new Map();
    const dedupe = (m) => {
        if (!m || typeof m !== "object") return m;
        let key;
        try { key = JSON.stringify(m); } catch { return m; }
        const hit = canonical.get(key);
        if (hit) return hit;
        canonical.set(key, m);
        return m;
    };
    const items = (Array.isArray(payload.items) ? payload.items : []).map((it) => {
        if (!it || typeof it !== "object" || !Array.isArray(it.snapshot)) return it;
        return { ...it, snapshot: it.snapshot.map(dedupe) };
    });
    const agentMessages = Array.isArray(payload.agentMessages)
        ? payload.agentMessages.map(dedupe)
        : payload.agentMessages;
    return { ...payload, items, agentMessages };
}

// chat.items may have `tool` / `kernel` / `turn` nodes whose status
// was "running" at save time. Whatever they were doing didn't
// survive the refresh, so we degrade them to a terminal state — a
// running `turn` in particular would surface "Agent · running…" in
// the header and make the chat look like it's still working.
//
// Also strips:
//   • `abort` closures (defensive — should be replaced by null at
//     JSON.stringify time, but a future code path might add new
//     fields we don't expect)
//   • `previewUrl` on user-message images. previewUrl is just
//     `data:<mimeType>;base64,<data>` — pure derivative of the
//     other two fields. Persisting both doubles the storage cost
//     of every image. We reconstruct it on hydrate.
function stripTransientFromItems(items) {
    if (!Array.isArray(items)) return [];
    return items.map((it) => {
        if (!it || typeof it !== "object") return it;
        if (it.kind === "tool" && it.status === "running") {
            return { ...it, status: "stopped", abort: null };
        }
        if (it.kind === "kernel" && it.status === "running") {
            return { ...it, status: "error", error: "interrupted by page refresh", abort: null };
        }
        if (it.kind === "turn") {
            // Tool items live inside turn.children — recurse so a
            // mid-stream save with a "running" tool deep in the tree
            // still gets downgraded.
            const next = { ...it };
            if (it.status === "running") {
                next.status = "done";
                next.stopReason = it.stopReason || "page_refresh";
            }
            if (Array.isArray(it.children) && it.children.length) {
                next.children = stripTransientFromItems(it.children);
            }
            return next;
        }
        if (it.kind === "user" && Array.isArray(it.images) && it.images.length) {
            return {
                ...it,
                images: it.images.map((img) => {
                    if (!img || typeof img !== "object") return img;
                    if (!img.previewUrl) return img;
                    const { previewUrl: _drop, ...rest } = img;
                    return rest;
                }),
            };
        }
        return it;
    });
}

// Reverse of the previewUrl stripping in stripTransientFromItems —
// reconstruct the data URL the UI's <img src> reads from. Only
// touches `user`-kind items with images. Idempotent.
//
// Exported because the load-time pass is not the only place it has
// to run: an image whose base64 was large enough to be externalized
// comes back from the server as a blob PLACEHOLDER, so `img.data` is
// still an object when this runs on load and the guard below declines.
// Blob hydration then fills `img.data` in, and the image needs this
// applied again or it renders `<img src={undefined}>` forever with
// its bytes sitting in memory.
export function restoreImagePreviewUrls(items) {
    if (!Array.isArray(items)) return [];
    return items.map((it) => {
        if (!it || typeof it !== "object") return it;
        if (it.kind !== "user" || !Array.isArray(it.images) || !it.images.length) return it;
        return {
            ...it,
            images: it.images.map((img) => {
                if (!img || typeof img !== "object") return img;
                if (img.previewUrl) return img;
                if (typeof img.data === "string" && typeof img.mimeType === "string") {
                    return { ...img, previewUrl: `data:${img.mimeType};base64,${img.data}` };
                }
                return img;
            }),
        };
    });
}

// Strip closures / DOM nodes / circular refs via the shared replacer.
// JSON-round-trip is deliberate: it normalizes shape (no Date/Map/Set
// surprises) and produces a fresh object tree decoupled from React /
// pi-agent live state.
function jsonClean(value) {
    if (value == null) return value;
    try {
        return JSON.parse(JSON.stringify(value, makeReplacer()));
    } catch (err) {
        console.warn("[chatSerializer] jsonClean failed; returning null", err);
        return null;
    }
}
